import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Table from "react-bootstrap/Table";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";

const API_BASE = "https://app.vivasmartcare.com/api";
const ROWS_PER_PAGE = 30;
const ALARMS_PER_PAGE = 20;

const SLEEP_STAGE_MAP = {
  "0": "Derin Uyku",
  "1": "Hafif Uyku",
  "2": "Uyanık",
  "3": "Yatak Dışı",
  "4": "Çevrimdışı",
  "7": "Göz Hareketi",
};

const ALARM_EVENT_TYPE_MAP = {
  11: "Hızlı Solunum",
  12: "Yüzeysel Solunum",
  13: "Solunum Durması",
  14: "Yüksek Kalp Atışı",
  15: "Düşük Kalp Atışı",
};

const LOCAL_ALARM_EVENT_MAP = {
  "Enter room": "Odaya Girdi",
  "Leave room": "Odadan Çıktı",
  "Enter area": "Yatağa Girdi",
  "Leave area": "Yataktan Çıktı",
  "Suspected fall": "Şüpheli Düşme",
  "Fall confirmation": "Düşme Onayı",
  "Heart rate abnormal": "Anormal Kalp Atışı",
  "Breath rate abnormal": "Anormal Solunum",
};

function fmtDate(d) {
  return d.toLocaleString("tr-TR", {
    timeZone: "Europe/Istanbul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function localDateString(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function enumerateDays(startStr, endStr) {
  const start = new Date(startStr);
  const end = new Date(endStr);
  if (isNaN(start) || isNaN(end) || start > end) return [];
  const days = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  while (cur <= last) {
    days.push(localDateString(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return days;
}

function buildDataItems(report, date) {
  const ts = report.timestamps || [];
  const br = report.breathRateVo?.dataList || [];
  const hr = report.heartRateVo?.dataList || [];
  const stages = report.statisticalData || [];

  let baseDate;
  if (report.getBedIdx && /^\d{4}-\d{2}-\d{2} /.test(report.getBedIdx)) {
    baseDate = new Date(report.getBedIdx.replace(" ", "T"));
    baseDate = new Date(
      baseDate.getFullYear(),
      baseDate.getMonth(),
      baseDate.getDate()
    );
  } else {
    const [y, m, d] = date.split("-").map(Number);
    baseDate = new Date(y, m - 1, d - 1);
  }

  const stageRanges = stages
    .map((s) => {
      const start = s.startTime ? new Date(s.startTime.replace(" ", "T")) : null;
      const end = s.endTime ? new Date(s.endTime.replace(" ", "T")) : null;
      return start && end ? { start, end, status: s.status } : null;
    })
    .filter(Boolean);

  const findStage = (dt) => {
    for (const r of stageRanges) {
      if (dt >= r.start && dt < r.end) return r.status;
    }
    return null;
  };

  const items = [];
  let prevHour = -1;
  let dayOffset = 0;
  for (let i = 0; i < ts.length; i++) {
    const parts = String(ts[i]).split(":").map(Number);
    if (parts.length < 2 || parts.some(isNaN)) continue;
    const [hh, mm, ss = 0] = parts;
    if (prevHour !== -1 && hh < prevHour - 2) dayOffset++;
    prevHour = hh;
    const itemDate = new Date(baseDate);
    itemDate.setDate(itemDate.getDate() + dayOffset);
    itemDate.setHours(hh, mm, ss, 0);
    const stage = findStage(itemDate);
    items.push({
      time: itemDate.toISOString(),
      hr: hr[i] ? Number(hr[i]) : null,
      rr: br[i] ? Number(br[i]) : null,
      sleepStage: stage,
    });
  }
  return items;
}

function extractAlarmEvents(report) {
  if (!report?.alarmEvents) return [];
  const events = Array.isArray(report.alarmEvents)
    ? report.alarmEvents
    : Object.values(report.alarmEvents);
  return events.map((e) => ({
    time: e.ts ? new Date(e.ts.replace(" ", "T")).toISOString() : null,
    eventType: e.eventType,
    label: ALARM_EVENT_TYPE_MAP[e.eventType] || e.eventName || "Bilinmeyen Olay",
  }));
}

const HobacareReportPage = () => {
  const { deviceId: paramDeviceId } = useParams();
  const navigate = useNavigate();

  const [inputId, setInputId] = useState(paramDeviceId || "");
  const [submittedId, setSubmittedId] = useState(paramDeviceId || "");

  const [startDatetime, setStartDatetime] = useState("");
  const [endDatetime, setEndDatetime] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasQueried, setHasQueried] = useState(false);
  const [vitalItems, setVitalItems] = useState([]);
  const [reportAlarms, setReportAlarms] = useState([]);
  const [dailySummaries, setDailySummaries] = useState([]);
  const [localAlarms, setLocalAlarms] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [vitalPage, setVitalPage] = useState(1);
  const [alarmPage, setAlarmPage] = useState(1);

  useEffect(() => {
    setInputId(paramDeviceId || "");
    setSubmittedId(paramDeviceId || "");
  }, [paramDeviceId]);

  const datesValid = useMemo(() => {
    if (!startDatetime || !endDatetime) return false;
    return new Date(startDatetime) <= new Date(endDatetime);
  }, [startDatetime, endDatetime]);

  const handleIdSubmit = (e) => {
    e?.preventDefault();
    const trimmed = inputId.trim();
    if (!trimmed) return;
    setSubmittedId(trimmed);
    navigate(`/cihaz-rapor/${encodeURIComponent(trimmed)}`, {
      replace: false,
    });
  };

  const handleReset = () => {
    setSubmittedId("");
    setInputId("");
    setStartDatetime("");
    setEndDatetime("");
    setVitalItems([]);
    setReportAlarms([]);
    setDailySummaries([]);
    setLocalAlarms([]);
    setWarnings([]);
    setHasQueried(false);
    navigate("/cihaz-rapor");
  };

  const fetchLocalAlarms = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/alarm-detail/${id}`);
      const all = await r.json();
      const startMs = new Date(startDatetime).getTime();
      const endMs = new Date(endDatetime).getTime();
      const filtered = (Array.isArray(all) ? all : [])
        .filter((a) => a.alarmScenario === "hobacare")
        .filter((a) => {
          const t = new Date(a.endTimestamp || a.createdAt).getTime();
          if (isNaN(t)) return false;
          return t >= startMs && t <= endMs;
        });
      setLocalAlarms(filtered);
    } catch (e) {
      console.error("Local AlarmLog çekme hatası:", e);
    }
  };

  const fetchData = async () => {
    if (!submittedId) return;
    setLoading(true);
    setHasQueried(true);
    setWarnings([]);
    try {
      const days = enumerateDays(startDatetime, endDatetime);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const cutoff = new Date(today.getTime() - 14 * 86400000);
      const acceptedDays = [];
      const skippedDays = [];
      for (const d of days) {
        const [y, m, dd] = d.split("-").map(Number);
        const dt = new Date(y, m - 1, dd);
        if (dt < cutoff) skippedDays.push(d);
        else acceptedDays.push(d);
      }
      if (skippedDays.length > 0) {
        setWarnings((w) => [
          ...w,
          `Sistem 14 günden eski veriyi saklamıyor. Atlanılan günler: ${skippedDays.join(", ")}`,
        ]);
      }

      const startMs = new Date(startDatetime).getTime();
      const endMs = new Date(endDatetime).getTime();
      const allItems = [];
      const allAlarms = [];
      const summaries = [];

      for (const date of acceptedDays) {
        try {
          const r = await fetch(
            `${API_BASE}/hobacare/sleep-report?uid=${encodeURIComponent(
              submittedId
            )}&date=${date}`
          );
          const j = await r.json();
          if (String(j.code) !== "200") {
            setWarnings((w) => [...w, `${date}: ${j.msg || "veri alınamadı"}`]);
            continue;
          }
          const report = Array.isArray(j.data) ? j.data[0] : j.data;
          if (!report) continue;

          summaries.push({
            date,
            score: report.score ?? "-",
            sleepTotalTime: report.sleepTotalTime ?? "-",
            onBedTime: report.onBedTime ?? "-",
            leaveBedCount: report.leaveBedCount ?? "-",
            scoreLabel: report.scoreLabel || "-",
            ahi: report.ahi || "-",
            breathAvg: report.breathRateVo?.avg ?? "-",
            breathMin: report.breathRateVo?.min ?? "-",
            breathMax: report.breathRateVo?.max ?? "-",
            heartAvg: report.heartRateVo?.avg ?? "-",
            heartMin: report.heartRateVo?.min ?? "-",
            heartMax: report.heartRateVo?.max ?? "-",
          });

          const dayItems = buildDataItems(report, date);
          allItems.push(...dayItems);
          allAlarms.push(...extractAlarmEvents(report));
        } catch (e) {
          setWarnings((w) => [...w, `${date}: ${e.message}`]);
        }
      }

      const filteredItems = allItems
        .filter((it) => {
          const t = new Date(it.time).getTime();
          return t >= startMs && t <= endMs;
        })
        .sort((a, b) => new Date(a.time) - new Date(b.time));

      const filteredAlarms = allAlarms
        .filter((a) => {
          if (!a.time) return true;
          const t = new Date(a.time).getTime();
          return t >= startMs && t <= endMs;
        })
        .sort((a, b) => new Date(b.time) - new Date(a.time));

      setVitalItems(filteredItems);
      setReportAlarms(filteredAlarms);
      setDailySummaries(summaries);
      setVitalPage(1);
      setAlarmPage(1);

      await fetchLocalAlarms(submittedId);
    } catch (error) {
      console.error("Hobacare veri çekme hatası:", error);
      setWarnings((w) => [...w, error.message]);
    } finally {
      setLoading(false);
    }
  };

  const combinedAlarms = useMemo(() => {
    const reportRows = reportAlarms.map((a) => ({
      time: a.time,
      label: a.label,
      source: "Cihaz Raporu",
    }));
    const localRows = localAlarms.map((a) => ({
      time: a.endTimestamp || a.createdAt,
      label: LOCAL_ALARM_EVENT_MAP[a.eventType] || a.eventType,
      source: "Sistem Logu",
      description: a.description,
    }));
    return [...reportRows, ...localRows].sort(
      (a, b) => new Date(b.time) - new Date(a.time)
    );
  }, [reportAlarms, localAlarms]);

  const vitalTotalPages = Math.max(1, Math.ceil(vitalItems.length / ROWS_PER_PAGE));
  const vitalPageData = useMemo(() => {
    const i = (vitalPage - 1) * ROWS_PER_PAGE;
    return vitalItems.slice(i, i + ROWS_PER_PAGE);
  }, [vitalItems, vitalPage]);

  const alarmTotalPages = Math.max(1, Math.ceil(combinedAlarms.length / ALARMS_PER_PAGE));
  const alarmPageData = useMemo(() => {
    const i = (alarmPage - 1) * ALARMS_PER_PAGE;
    return combinedAlarms.slice(i, i + ALARMS_PER_PAGE);
  }, [combinedAlarms, alarmPage]);

  const downloadExcel = () => {
    if (vitalItems.length === 0) return;
    const headers = ["Zaman", "Nabız (bpm)", "Solunum (rpm)", "Uyku Durumu"];
    const rows = vitalItems.map((it) => [
      fmtDate(new Date(it.time)),
      it.hr ?? "",
      it.rr ?? "",
      SLEEP_STAGE_MAP[it.sleepStage] || "",
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["﻿" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `vital-${submittedId}.csv`;
    a.click();
  };

  const downloadPDF = () => {
    if (vitalItems.length === 0) return;
    const doc = new jsPDF("l", "pt", "a4");
    doc.setFontSize(14);
    doc.text(`Vital Rapor - ${submittedId}`, 40, 40);
    doc.setFontSize(9);
    doc.text(
      `Tarih: ${new Date().toLocaleString("tr-TR")} | Toplam: ${vitalItems.length} kayit`,
      40,
      55
    );
    autoTable(doc, {
      head: [["Zaman", "Nabiz (bpm)", "Solunum (rpm)", "Uyku Durumu"]],
      body: vitalItems.map((it) => [
        fmtDate(new Date(it.time)),
        it.hr ?? "-",
        it.rr ?? "-",
        SLEEP_STAGE_MAP[it.sleepStage] || "-",
      ]),
      startY: 70,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [22, 160, 133] },
    });
    doc.save(`vital-${submittedId}.pdf`);
  };

  const downloadAlarmsExcel = () => {
    if (combinedAlarms.length === 0) return;
    const headers = ["Sıra", "Zaman", "Olay", "Kaynak", "Açıklama"];
    const rows = combinedAlarms.map((a, i) => [
      i + 1,
      a.time ? fmtDate(new Date(a.time)) : "-",
      a.label,
      a.source,
      a.description || "-",
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `alarmlar-${submittedId}.csv`;
    a.click();
  };

  const downloadAlarmsPDF = () => {
    if (combinedAlarms.length === 0) return;
    const doc = new jsPDF("l", "pt", "a4");
    doc.setFontSize(14);
    doc.text(`Alarm Rapor - ${submittedId}`, 40, 40);
    doc.setFontSize(9);
    doc.text(
      `Tarih: ${new Date().toLocaleString("tr-TR")} | Toplam: ${combinedAlarms.length} kayit`,
      40,
      55
    );
    autoTable(doc, {
      head: [["Sira", "Zaman", "Olay", "Kaynak", "Aciklama"]],
      body: combinedAlarms.map((a, i) => [
        i + 1,
        a.time ? fmtDate(new Date(a.time)) : "-",
        a.label,
        a.source,
        a.description || "-",
      ]),
      startY: 70,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [192, 57, 43] },
    });
    doc.save(`alarmlar-${submittedId}.pdf`);
  };

  // ============== Render ==============

  // Adım 1: Cihaz ID girilmemişse — sadece input + buton
  if (!submittedId) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#f5f7fa",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
        }}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: "12px",
            padding: "40px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
            width: "100%",
            maxWidth: "440px",
          }}
        >
          <h4 style={{ marginBottom: "8px", color: "#222" }}>Cihaz Raporu</h4>
          <p style={{ color: "#888", fontSize: "0.9rem", marginBottom: "24px" }}>
            Görüntülemek istediğiniz cihazın ID'sini girin.
          </p>
          <Form onSubmit={handleIdSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Cihaz ID</Form.Label>
              <Form.Control
                type="text"
                placeholder="Örn. 414D7418B647"
                value={inputId}
                onChange={(e) => setInputId(e.target.value)}
                autoFocus
              />
            </Form.Group>
            <Button
              type="submit"
              variant="primary"
              disabled={!inputId.trim()}
              style={{ width: "100%" }}
            >
              Görüntüle
            </Button>
          </Form>
        </div>
      </div>
    );
  }

  // Adım 2: Cihaz ID seçilmiş — tam rapor görünümü
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f5f7fa",
        padding: "20px",
      }}
    >
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          background: "#fff",
          borderRadius: "12px",
          padding: "24px",
          boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <div>
            <h4 style={{ marginBottom: "4px" }}>Cihaz Raporu</h4>
            <small style={{ color: "#888" }}>Cihaz ID: {submittedId}</small>
          </div>
          <Button variant="outline-secondary" size="sm" onClick={handleReset}>
            Farklı Cihaz Sorgula
          </Button>
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
            alignItems: "flex-end",
            marginBottom: "16px",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                fontSize: "0.85rem",
                color: "#555",
                marginBottom: "4px",
              }}
            >
              Başlangıç Tarihi
            </label>
            <Form.Control
              type="datetime-local"
              value={startDatetime}
              onChange={(e) => setStartDatetime(e.target.value)}
              max={endDatetime || undefined}
              style={{ minWidth: "200px" }}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                fontSize: "0.85rem",
                color: "#555",
                marginBottom: "4px",
              }}
            >
              Bitiş Tarihi
            </label>
            <Form.Control
              type="datetime-local"
              value={endDatetime}
              onChange={(e) => setEndDatetime(e.target.value)}
              min={startDatetime || undefined}
              style={{ minWidth: "200px" }}
            />
          </div>
          <Button
            variant="primary"
            onClick={fetchData}
            disabled={!datesValid || loading}
          >
            {loading ? "Yükleniyor..." : "Sorgula"}
          </Button>
          {startDatetime && endDatetime && !datesValid && (
            <p style={{ color: "#e74c3c", margin: 0, fontSize: "0.85rem" }}>
              ⚠️ Başlangıç tarihi bitiş tarihinden sonra olamaz.
            </p>
          )}
        </div>

        {warnings.length > 0 && (
          <div
            style={{
              background: "#fff3cd",
              border: "1px solid #ffeeba",
              color: "#856404",
              borderRadius: "8px",
              padding: "10px 14px",
              marginBottom: "16px",
              fontSize: "0.9rem",
            }}
          >
            {warnings.map((w, i) => (
              <div key={i}>⚠️ {w}</div>
            ))}
          </div>
        )}

        <Tabs defaultActiveKey="vital" className="mb-3">
          <Tab eventKey="vital" title="Vital Değerler">
            <div
              style={{
                display: "flex",
                gap: "8px",
                marginBottom: "16px",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="success"
                size="sm"
                onClick={downloadExcel}
                disabled={vitalItems.length === 0}
              >
                Excel İndir
              </Button>
              <Button
                variant="success"
                size="sm"
                onClick={downloadPDF}
                disabled={vitalItems.length === 0}
              >
                PDF İndir
              </Button>
            </div>

            {dailySummaries.length > 0 && (
              <div style={{ marginBottom: "20px" }}>
                <h6>Günlük Özet</h6>
                <Table striped bordered hover size="sm" responsive>
                  <thead>
                    <tr>
                      <th>Tarih</th>
                      <th>Uyku Skoru</th>
                      <th>Toplam Uyku (dk)</th>
                      <th>Yatakta (dk)</th>
                      <th>Yataktan Kalkma</th>
                      <th>AHI</th>
                      <th>Nabız (min/avg/max)</th>
                      <th>Solunum (min/avg/max)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailySummaries.map((s) => (
                      <tr key={s.date}>
                        <td>{s.date}</td>
                        <td>{s.score}</td>
                        <td>{s.sleepTotalTime}</td>
                        <td>{s.onBedTime}</td>
                        <td>{s.leaveBedCount}</td>
                        <td>{s.ahi}</td>
                        <td>
                          {s.heartMin}/{s.heartAvg}/{s.heartMax}
                        </td>
                        <td>
                          {s.breathMin}/{s.breathAvg}/{s.breathMax}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {hasQueried && vitalItems.length === 0 && !loading && (
              <p style={{ color: "#999" }}>
                Seçilen aralıkta vital veri bulunamadı.
              </p>
            )}

            {vitalItems.length > 0 && (
              <>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Zaman</th>
                      <th>Nabız (bpm)</th>
                      <th>Solunum (rpm)</th>
                      <th>Uyku Durumu</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vitalPageData.map((it, i) => (
                      <tr key={i}>
                        <td>{fmtDate(new Date(it.time))}</td>
                        <td>{it.hr ?? "-"}</td>
                        <td>{it.rr ?? "-"}</td>
                        <td>{SLEEP_STAGE_MAP[it.sleepStage] || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Button
                    size="sm"
                    onClick={() => setVitalPage((p) => Math.max(1, p - 1))}
                    disabled={vitalPage === 1}
                  >
                    Önceki
                  </Button>
                  <span>
                    {vitalPage} / {vitalTotalPages} ({vitalItems.length} kayıt)
                  </span>
                  <Button
                    size="sm"
                    onClick={() =>
                      setVitalPage((p) => Math.min(vitalTotalPages, p + 1))
                    }
                    disabled={vitalPage >= vitalTotalPages}
                  >
                    Sonraki
                  </Button>
                </div>
              </>
            )}
          </Tab>

          <Tab eventKey="alarms" title="Alarm Bilgileri">
            <div
              style={{
                display: "flex",
                gap: "8px",
                marginBottom: "16px",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="success"
                size="sm"
                onClick={downloadAlarmsExcel}
                disabled={combinedAlarms.length === 0}
              >
                Excel İndir
              </Button>
              <Button
                variant="success"
                size="sm"
                onClick={downloadAlarmsPDF}
                disabled={combinedAlarms.length === 0}
              >
                PDF İndir
              </Button>
            </div>

            {hasQueried && combinedAlarms.length === 0 && !loading && (
              <p style={{ color: "#999" }}>
                Seçilen aralıkta alarm bulunamadı.
              </p>
            )}

            {combinedAlarms.length > 0 && (
              <>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Sıra</th>
                      <th>Zaman</th>
                      <th>Olay</th>
                      <th>Kaynak</th>
                      <th>Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alarmPageData.map((a, i) => (
                      <tr key={i}>
                        <td>{(alarmPage - 1) * ALARMS_PER_PAGE + i + 1}</td>
                        <td>{a.time ? fmtDate(new Date(a.time)) : "-"}</td>
                        <td>{a.label}</td>
                        <td>{a.source}</td>
                        <td>{a.description || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Button
                    size="sm"
                    onClick={() => setAlarmPage((p) => Math.max(1, p - 1))}
                    disabled={alarmPage === 1}
                  >
                    Önceki
                  </Button>
                  <span>
                    {alarmPage} / {alarmTotalPages} ({combinedAlarms.length} kayıt)
                  </span>
                  <Button
                    size="sm"
                    onClick={() =>
                      setAlarmPage((p) => Math.min(alarmTotalPages, p + 1))
                    }
                    disabled={alarmPage >= alarmTotalPages}
                  >
                    Sonraki
                  </Button>
                </div>
              </>
            )}
          </Tab>
        </Tabs>
      </div>
    </div>
  );
};

export default HobacareReportPage;
